import React from 'react';
import { useIntl } from 'react-intl';
import { isHiragana } from 'wanakana';
import KeyboardReact, { KeyboardReactInterface } from 'react-simple-keyboard';

import { BUTTON_LABELS } from '../../utils/KeyUtils';
import japaneseModule from './modules';
import candidatesModule from 'components/Keyboard/modules/candidates';
import getJapaneseCandidates from 'components/Keyboard/Custom/Japanese/layouts/provider';
import { SPECIAL_KEYS } from 'components/Keyboard/utils/constants';
import { CandidatesKeyboard, KeyboardLayoutType } from 'components/Keyboard/types';

import { sidebarLayout, actionLayout } from './layouts/sidebar';
import type { KeyboardComponentProps } from 'components/Keyboard/types';
import ComposingState from 'components/Keyboard/cache/composing';
import SegmentComposer from './modules/segmentComposer';

import './japanese.css';

const BUTTON_THEME = [
  {
    class: 'keyboard-divider',
    buttons: SPECIAL_KEYS.DIVIDER,
  },
  {
    class: 'col-span-2',
    buttons: '{kaomoji} {zenkaku} {shift} {bksp}',
  },
  {
    class: 'col-span-4',
    buttons: '{shift}',
  },
  {
    class: '!w-auto !max-w-none',
    buttons: '@',
  },
];

type JapaneseImeKeyboardProps = KeyboardComponentProps & {
  onToggle: () => void;
};

const JapaneseImeKeyboard: React.FC<JapaneseImeKeyboardProps> = ({
  type,
  language,
  layoutType,
  layoutCandidates,
  onInit,
  onChange,
  keyboardRef,
  onKeyReleased,
  onSelectionChange,
  onToggle,
}) => {
  const intl = useIntl();

  const actionRef = React.useRef<KeyboardReactInterface>(null);
  const sidebarRef = React.useRef<KeyboardReactInterface>(null);
  const mainKeyboardRef = React.useRef<CandidatesKeyboard>(null);

  const display = React.useMemo(() => BUTTON_LABELS(intl), [intl]);

  const actionDisplay = React.useMemo(() => ({
    ...BUTTON_LABELS(intl),
    // Override display for special keys for Japanese keyboard
    [SPECIAL_KEYS.SPACE]: '空白',
  }), [intl]);

  const isAlternateLayout = layoutType === KeyboardLayoutType.ALTERNATE;
  const initialLayout = isAlternateLayout ? SPECIAL_KEYS.ABC : SPECIAL_KEYS.KANA;

  const [hasCandidateSelected, setHasCandidateSelected] = React.useState(false);
  const [activeScreen, setActiveScreen] = React.useState<string>(initialLayout);
  const [segmentMode, setSegmentMode] = React.useState(false);
  const lastReadingRef = React.useRef('');

  const setSidebarRef = React.useCallback((ref: KeyboardReactInterface) => {
    sidebarRef.current = ref;
  }, []);

  const setMainRef = React.useCallback(
    (ref: KeyboardReactInterface) => {
      mainKeyboardRef.current = ref as CandidatesKeyboard;
      keyboardRef(ref);
      (ref as CandidatesKeyboard).onCandidatesChange = (hasCandidates: boolean) => {
        setHasCandidateSelected(false);
        actionRef.current?.setOptions({
          layoutName: hasCandidates ? 'withCandidates' : 'default',
        });
      };
    },
    [keyboardRef],
  );

  const setActionRef = React.useCallback((ref: KeyboardReactInterface) => {
    actionRef.current = ref;
  }, []);

  // The preview shown to the customer is the fully-assembled string (each
  // segment's currently selected candidate), with the active segment
  // underlined via onSelectionChange.
  const updatePreview = React.useCallback(() => {
    const kb = mainKeyboardRef.current;
    if (!kb) {
      return;
    }
    const reading = ComposingState.get();
    const prefixLength = kb.getInput().length - reading.length;
    const prefix = kb.getInput().slice(0, prefixLength);
    onChange(prefix + SegmentComposer.getAssembled());
    const [start, end] = SegmentComposer.getActiveRange();
    onSelectionChange([prefixLength + start, prefixLength + end]);
  }, [onChange, onSelectionChange]);

  const commitSegments = React.useCallback(() => {
    const kb = mainKeyboardRef.current;
    if (!kb) {
      return;
    }
    const reading = ComposingState.get();
    const newInput = reading ? kb.getInput().slice(0, -reading.length) + SegmentComposer.getAssembled() : kb.getInput();
    kb.setInput(newInput);
    kb.setCaretPosition(newInput.length);
    kb.flushCandidates();
    onChange(newInput);
    onSelectionChange([newInput.length, newInput.length]);
    SegmentComposer.reset();
    setSegmentMode(false);
    kb.setOptions({ enableLayoutCandidates: true });
    actionRef.current?.setOptions({ layoutName: 'default' });
  }, [onChange, onSelectionChange]);

  // Shows the active segment's own candidates in the existing candidate-box widget
  // (the same visible, tappable list the flat system used), instead of leaving the
  // customer to cycle blindly with {nextCandidate}.
  const showSegmentCandidates = React.useCallback(() => {
    const kb = mainKeyboardRef.current;
    if (!kb) {
      return;
    }
    const candidates = SegmentComposer.getActiveCandidates();
    kb.candidateBox?.destroy();
    if (!candidates.length) {
      return;
    }
    requestAnimationFrame(() => {
      kb.candidateBox?.show({
        candidateValue: candidates.join(' '),
        targetElement: kb.keyboardDOM,
        onSelect: (selected: string) => {
          const index = candidates.indexOf(selected);
          if (index >= 0) {
            SegmentComposer.selectCandidate(index);
          }
          // A single-segment word has nothing left to correct, so selecting
          // its candidate commits immediately, matching the old one-tap
          // behavior. A multi-segment phrase still needs an explicit confirm
          // so the customer can keep fixing the other segments first.
          if (SegmentComposer.segments.length === 1) {
            commitSegments();
            return;
          }
          updatePreview();
          showSegmentCandidates();
        },
      });
      // The library only renders the list; it has no notion of a "current"
      // candidate, so highlight it ourselves (mirrors candidates.ts's own
      // highlightCandidate, scoped to a freshly (re)rendered box).
      const selectedIndex = SegmentComposer.getActiveSelection();
      if (selectedIndex >= 0) {
        const pageSize = kb.candidateBox?.pageSize || candidates.length;
        const targetPage = Math.floor(selectedIndex / pageSize);
        const container = kb.candidateBox?.candidateBoxElement;
        for (let page = 0; page < targetPage; page += 1) {
          container?.querySelector('.hg-candidate-box-next')?.dispatchEvent(new MouseEvent('click'));
        }
        container?.querySelectorAll('.hg-candidate-box-list-item')[selectedIndex % pageSize]?.classList.add('selected');
      }
    });
  }, [updatePreview, commitSegments]);

  const refreshSegments = React.useCallback(async (reading: string) => {
    // Mozc's convert() expects a kana reading; feeding it raw romaji doesn't
    // fail cleanly, it echoes the typed text back as a single pass-through
    // "segment", fighting with the flat system's own (working) romaji→kana
    // candidates for control of the preview. Segment mode is reserved for
    // the kana-typed workflow; anything else falls through to the flat system.
    if (!reading || !isHiragana(reading)) {
      SegmentComposer.reset();
      setSegmentMode(false);
      return;
    }
    await SegmentComposer.setReading(reading);
    if (SegmentComposer.hasSegments()) {
      setSegmentMode(true);
      // Deliberately NOT disabling enableLayoutCandidates: that flag also gates
      // ComposingState tracking in candidates.ts, so turning it off freezes the
      // reading after the first character. Just clobber the old candidate box
      // with ours instead (see showSegmentCandidates).
      actionRef.current?.setOptions({ layoutName: 'segments' });
      updatePreview();
      showSegmentCandidates();
    }
  }, [updatePreview, showSegmentCandidates]);

  // Polls ComposingState because keystrokes on the main kana grid go straight
  // through candidatesModule's own handleButtonClicked override (shared with the
  // Chinese keyboards), with no hook exposed here to react to them directly.
  React.useEffect(() => {
    const interval = setInterval(() => {
      const reading = ComposingState.get();
      if (reading !== lastReadingRef.current) {
        lastReadingRef.current = reading;
        refreshSegments(reading);
      }
    }, 150);
    return () => clearInterval(interval);
  }, [refreshSegments]);

  const handlePanelKeyPress = React.useCallback((button: string) => {
    if (segmentMode) {
      if (button === SPECIAL_KEYS.PREV_SEGMENT) {
        SegmentComposer.prevSegment();
        updatePreview();
        showSegmentCandidates();
        return;
      }
      if (button === SPECIAL_KEYS.NEXT_SEGMENT) {
        SegmentComposer.nextSegment();
        updatePreview();
        showSegmentCandidates();
        return;
      }
      if (button === SPECIAL_KEYS.NEXT_CANDIDATE) {
        SegmentComposer.cycleCandidate();
        updatePreview();
        showSegmentCandidates();
        return;
      }
      if (button === SPECIAL_KEYS.ENTER) {
        commitSegments();
        return;
      }
    }

    // Switch from the Japanese keyboard to the full-frame QWERTY keyboard.
    if (button === SPECIAL_KEYS.NEXT_KEYBOARD) {
      mainKeyboardRef.current?.flushCandidates();
      SegmentComposer.reset();
      setSegmentMode(false);
      onToggle();
      return;
    }

    if ([SPECIAL_KEYS.NUMBERS, SPECIAL_KEYS.ABC, SPECIAL_KEYS.KANA].includes(button as SPECIAL_KEYS)) {
      setActiveScreen(button);
    }

    if (button === SPECIAL_KEYS.NEXT_CANDIDATE) {
      const selected = mainKeyboardRef.current?.selectNextCandidate();
      if (selected) {
        setHasCandidateSelected(true);
      }
      return;
    }

    if (button === SPECIAL_KEYS.ENTER) {
      if (hasCandidateSelected) {
        mainKeyboardRef.current?.confirmSelectedCandidate();
        setHasCandidateSelected(false);
      }
      // {enter} is disabled when no candidate is selected
      return;
    }

    mainKeyboardRef.current?.handleButtonClicked(button);
  }, [hasCandidateSelected, onToggle, segmentMode, updatePreview, showSegmentCandidates, commitSegments]);

  const actionButtonThemes = React.useMemo(
    () => (hasCandidateSelected || segmentMode) ? [] : [{ class: 'disabled', buttons: SPECIAL_KEYS.ENTER }],
    [hasCandidateSelected, segmentMode],
  );

  const modules = React.useMemo(
    () => [candidatesModule(getJapaneseCandidates), japaneseModule(initialLayout)],
    [initialLayout],
  );

  const sidebarButtonThemes = React.useMemo(
    () => [
      {
        class: '!bg-gray-300',
        buttons: activeScreen,
      },
    ],
    [activeScreen],
  );

  return (
    <div className="flex gap-4">
      <KeyboardReact
        mergeDisplay
        display={display}
        layout={sidebarLayout}
        preventMouseDownDefault
        keyboardRef={setSidebarRef}
        onKeyPress={handlePanelKeyPress}
        buttonTheme={sidebarButtonThemes}
        theme="hg-theme-default fb-keyboard japanese-left"
      />
      <KeyboardReact
        type={type}
        mergeDisplay
        newLineOnEnter
        onInit={onInit}
        modules={modules}
        display={display}
        onChange={onChange}
        language={language}
        layoutType={layoutType}
        keyboardRef={setMainRef}
        buttonTheme={BUTTON_THEME}
        onKeyReleased={onKeyReleased}
        layoutCandidatesPageSize={10}
        layoutCandidatesCaseSensitiveMatch
        layoutCandidates={layoutCandidates}
        onSelectionChange={onSelectionChange}
        theme={`hg-theme-default fb-keyboard paginated-candidates japanese-middle ${activeScreen.match('^{(.*)}$')?.at(1) ?? ''}`}
      />
      <KeyboardReact
        mergeDisplay
        display={actionDisplay}
        layout={actionLayout}
        preventMouseDownDefault
        keyboardRef={setActionRef}
        onKeyPress={handlePanelKeyPress}
        buttonTheme={actionButtonThemes}
        theme="hg-theme-default fb-keyboard japanese-right"
      />
    </div>
  );
};

export default JapaneseImeKeyboard;
